// Pure parallax math, kept separate from React so it can be unit-tested.

/** Pointer position normalized to the range [-1, 1] on each axis,
 *  measured from the center of the viewport. */
export interface Pointer {
  x: number;
  y: number;
}

/** Pixel translation to apply to a layer. */
export interface Offset {
  x: number;
  y: number;
}

/**
 * How far a layer drifts for a given pointer position.
 *
 * `depth` is a unitless factor: 0 = pinned (backdrop), larger = moves more.
 * `strength` is the maximum drift in pixels at depth 1 and full pointer deflection.
 * The vertical axis drifts less than the horizontal to feel natural.
 */
export function computeOffset(
  depth: number,
  pointer: Pointer,
  strength = 40
): Offset {
  // `+ 0` collapses -0 into 0 so callers never see a negative-zero translation.
  return {
    x: -pointer.x * depth * strength + 0,
    y: -pointer.y * depth * strength * 0.5 + 0,
  };
}

/** Linear interpolation used to smooth pointer motion frame-to-frame. */
export function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}

/**
 * Convert a clientX/clientY (e.g. from a mousemove event) into a centered,
 * normalized pointer in [-1, 1]. Width/height are the viewport dimensions.
 */
export function normalizePointer(
  clientX: number,
  clientY: number,
  width: number,
  height: number
): Pointer {
  if (width <= 0 || height <= 0) return { x: 0, y: 0 };
  return {
    x: (clientX / width) * 2 - 1,
    y: (clientY / height) * 2 - 1,
  };
}
